/**
 * Recorded SFX bank (public/audio/sfx, built by scripts/sfx). All variants are fetched and
 * decoded right after unlock (a few MB), leading silence is trimmed so hits are instant, and
 * each play is one AudioBufferSourceNode with a random variant and pitch variance.
 */
import type { AudioGraph } from './graph';

interface SfxEntry {
  files: string[];
  /** Linear gain multiplier. */
  gain?: number;
  /** Suggested reverb send 0..1. */
  rev?: number;
}

const BASE = `${import.meta.env.BASE_URL}audio/sfx/`;

export class SampleBank {
  private manifest: Record<string, SfxEntry> = {};
  private buffers = new Map<string, AudioBuffer[]>();
  private loading?: Promise<void>;
  private lastVariant = new Map<string, number>();

  constructor(private readonly graph: AudioGraph) {}

  get names(): string[] {
    return Object.keys(this.manifest);
  }

  has(name: string): boolean {
    return this.buffers.has(name);
  }

  known(name: string): boolean {
    return name in this.manifest;
  }

  load(): Promise<void> {
    this.loading ??= (async () => {
      try {
        const r = await fetch(`${BASE}manifest.json`);
        if (!r.ok) return;
        this.manifest = await r.json();
      } catch {
        return;
      }
      // Decode with limited concurrency; UI sounds first so menus feel alive immediately.
      const names = Object.keys(this.manifest).sort((a, b) => Number(b.startsWith('ui_')) - Number(a.startsWith('ui_')));
      let i = 0;
      const worker = async () => {
        while (i < names.length) {
          const name = names[i++];
          const bufs = await Promise.all(this.manifest[name].files.map((f) => this.decode(f)));
          const ok = bufs.filter((b): b is AudioBuffer => !!b);
          if (ok.length) this.buffers.set(name, ok);
        }
      };
      await Promise.all([worker(), worker(), worker(), worker()]);
    })();
    return this.loading;
  }

  private async decode(file: string): Promise<AudioBuffer | null> {
    try {
      const r = await fetch(BASE + file);
      if (!r.ok) return null;
      const buf = await this.graph.ctx.decodeAudioData(await r.arrayBuffer());
      return trimLead(this.graph.ctx, buf);
    } catch {
      return null;
    }
  }

  /**
   * Play a variant into `out` (+ `rev` send scaled by the entry's reverb amount).
   * Returns the end time, or false if the sound isn't loaded.
   */
  play(name: string, out: AudioNode, rev: GainNode | null, rate: number, when: number): number | false {
    const bufs = this.buffers.get(name);
    if (!bufs) return false;
    const e = this.manifest[name];
    // Never repeat the same variant twice in a row.
    let i = Math.floor(Math.random() * bufs.length);
    if (bufs.length > 1 && i === this.lastVariant.get(name)) i = (i + 1) % bufs.length;
    this.lastVariant.set(name, i);
    const buf = bufs[i];
    const ctx = this.graph.ctx;
    const src = ctx.createBufferSource();
    src.buffer = buf;
    src.playbackRate.value = rate;
    const g = ctx.createGain();
    g.gain.value = e?.gain ?? 1;
    src.connect(g).connect(out);
    if (rev && (e?.rev ?? 0) > 0) {
      rev.gain.value *= e!.rev!;
      g.connect(rev);
    }
    src.start(when);
    return when + buf.duration / rate;
  }
}

/** Drop leading near-silence (codec priming / sloppy edits) so every hit lands instantly. */
function trimLead(ctx: BaseAudioContext, b: AudioBuffer): AudioBuffer {
  const d = b.getChannelData(0);
  const max = Math.min(d.length, Math.floor(b.sampleRate * 0.12));
  let i = 0;
  while (i < max && Math.abs(d[i]) < 0.004) i++;
  i = Math.max(0, i - Math.floor(b.sampleRate * 0.001));
  if (i < 16) return b;
  const out = ctx.createBuffer(b.numberOfChannels, b.length - i, b.sampleRate);
  for (let c = 0; c < b.numberOfChannels; c++) out.copyToChannel(b.getChannelData(c).subarray(i), c);
  return out;
}
